import { useEffect, useReducer } from "react";

type DateState = {
  startDate: Date | undefined;
  endDate: Date | undefined;
};

interface RangeCalendarProps<T extends unknown[]> {
  startDate: Date | undefined;
  endDate: Date | undefined;
  onChange: (dates: DateState, ...extra: T) => void;
}

type PartialDateState = Partial<DateState>;

const dateReducer = (
  state: DateState,
  newState: PartialDateState
): DateState => {
  return { ...state, ...newState };
};

/**
 *  Hook to manage the state of a date range calendar
 */
export const useRangeCalendar = <T extends [unknown]>({
  startDate: initialStartDate,
  endDate: initialEndDate,
  onChange,
}: RangeCalendarProps<T>) => {
  const [{ startDate, endDate }, setDates] = useReducer(dateReducer, {
    startDate: initialStartDate,
    endDate: initialEndDate,
  });

  // Update the calendars internal state when the props change, without notifying the onChange
  useEffect(() => {
    setDates({
      startDate: initialStartDate,
      endDate: initialEndDate,
    });
  }, [initialStartDate, initialEndDate, setDates]);

  // HandleDateChange, accepts any number of extra arguments that are passed on to onChange.
  // Good for when you need to pass in extra data to the onChange handler without
  // making a whole new layer for this hook. Type them using the generic T.
  const handleDateChange = (dates: PartialDateState, ...extra: T) => {
    setDates(dates);
    onChange({ startDate, endDate, ...dates }, ...extra);
  };

  return {
    startDate,
    endDate,
    handleDateChange,
  };
};
